import secondaryComponents from "../../../../components/secondaryComponents";
import primaryComponents from "../../../../components/primaryComponents";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./ProfitAndLost.module.scss";
import useProfitAndLost from "./useProfitAndLost";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { pathConstants } from "../../../../routes/pathContants";
import { formatCurrency } from "../../../../helpers";
import { ExclamationCirlce } from "../../../../assets/svgIcons";

const ProfitAndLost = () => {
  const navigate = useNavigate();
  const {
    startDate,
    endDate,
    storeList,
    currentStore,
    profitAndLostData,
    isFetchingProfitAndLostData,
    storeHasMultipleCurrency,
    setCurrentStore,
    filterByStoreId,
    setDateFilter,
    isProfitAndLossDownloadWidget,
    setIsProfitAndLossDownloadWidget,
  } = useProfitAndLost();
  const labelTootipText =
    "From Wednesday 27th May 2026, new financial records will be captured in the new Accounting Module. Please use the Accounting Module for updated reports and export any historical data you may still need from this page.";
  const labelIcon = (
    <ExclamationCirlce width="18" height="18" stroke="#e02020" />
  );

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Profit and Lost Report"
      description="View profit and lost report"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0 }}
          className={styles.profitandloss_details}
        >
          <div className={styles.title_container}>
            <div className={styles.title_icon}>
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={() => navigate(pathConstants.ACCOUNTING_OVERVIEW)}
              >
                <FontAwesomeIcon
                  icon="angle-left"
                  style={{ fontSize: "18px" }}
                />
              </primaryComponents.Button>
              <div className={styles.title_content}>
                <h4 className="mb-0">Profit and Loss</h4>
                <p className={`${styles.sub_title} small-text-size`}>
                  Analyzing Profitability and Expenditure Trends
                </p>
                <div
                  style={{ marginTop: "10px", width: "100%" }}
                  className="error--squared"
                >
                  <secondaryComponents.ToolTipV2 tipText={labelTootipText}>
                    <span className="ml-2">{labelIcon}</span>
                    Warning: This report is being deprecated
                  </secondaryComponents.ToolTipV2>
                </div>
              </div>
            </div>

            <div>
              <primaryComponents.Button
                classNames="btn btn--outline"
                onClick={() => setIsProfitAndLossDownloadWidget(true)}
              >
                <FontAwesomeIcon icon="download" className="mr-2" />
                Export Profit & Loss Report
              </primaryComponents.Button>
            </div>
          </div>
          <div className={styles.card}>
            <div className={styles.picker_container}>
              <div className={styles.profitAndLost__storePicker}>
                <primaryComponents.SelectField
                  options={
                    storeHasMultipleCurrency
                      ? [...storeList]
                      : [
                          {
                            name: "All stores",
                            id: "ALL_STORES",
                          },
                          ...storeList,
                        ]
                  }
                  idKey="id"
                  nameKey="name"
                  onChange={(value) => {
                    setCurrentStore(value);
                    filterByStoreId(value);
                  }}
                  value={
                    storeList?.find(
                      (storeItem) => `${storeItem?.id}` === `${currentStore}`,
                    )?.name || "All stores"
                  }
                  floatError={false}
                  classNames="store-select"
                />
              </div>
              <div className={styles.profitAndLost__datePicker}>
                <primaryComponents.DateRangeInput
                  startDate={profitAndLostData?.start_date || startDate}
                  endDate={profitAndLostData?.end_date || endDate}
                  onChange={setDateFilter}
                  showPlaceholder={true}
                />
              </div>
            </div>

            {/* profit and lost */}
            <div className={styles.profitAndLost__summary}>
              <secondaryComponents.LoaderHelper
                isLoading={isFetchingProfitAndLostData}
                classNames="mt-4 mb-4"
              >
                <div className={styles.profitAndLost__summary_detail}>
                  <div
                    className={`${styles.header} d-flex justify-content-between align-items-center mb-1 m-1`}
                  >
                    <h5 className="pl-3 m-2 default-font-size">Gross Sales</h5>
                    <h5 className="pl-3 m-3 default-font-size">
                      {formatCurrency({
                        value: profitAndLostData?.revenues || 0,
                        currencyCode: profitAndLostData?.store_currency_code,
                      })}
                    </h5>
                  </div>
                  <hr />
                  <secondaryComponents.ProfitAndLostListItem
                    title={"Returns"}
                    value={profitAndLostData?.returns}
                    store_currency_code={profitAndLostData?.store_currency_code}
                  />
                  <hr />
                  {/* <secondaryComponents.ProfitAndLostListItem
                    title={"Discounts"}
                    value={profitAndLostData?.discounts}
                    store_currency_code={profitAndLostData?.store_currency_code}
                  /> */}
                  <secondaryComponents.Tree
                    data={profitAndLostData}
                    color={"#A5A6F6"}
                    title={"Net Sales"}
                    tipText={"Gross Sales - Sales Returns"}
                  />
                  <secondaryComponents.Tree
                    data={profitAndLostData}
                    color={"#FCDEB1"}
                    title={"Cost of Sales"}
                    tipText={
                      "Cost per Item Sold (Batch Sensitive) X Net Quantity Sold"
                    }
                  />
                  <secondaryComponents.Tree
                    data={profitAndLostData}
                    color={"#A5A6F6"}
                    title={"Gross Profit"}
                    tipText={"Net Sales - Cost of Sales"}
                  />
                  <hr />
                  <secondaryComponents.Tree
                    data={profitAndLostData}
                    color={"#FFFFFF"}
                    title={"Expenses"}
                  />
                  <hr />
                  <secondaryComponents.Tree
                    data={profitAndLostData}
                    color={"#C5C5C5"}
                    title={"Net Earnings Before Tax"}
                    tipText={
                      "Gross Profit - Expenses + Inventory Surplus - Inventory Loss"
                    }
                  />
                </div>
              </secondaryComponents.LoaderHelper>
            </div>
          </div>

          <secondaryComponents.Modal
            isActive={isProfitAndLossDownloadWidget}
            closeModal={() => setIsProfitAndLossDownloadWidget(false)}
          >
            <secondaryComponents.ExportProfitAndLossReport
              closeModal={() => setIsProfitAndLossDownloadWidget(false)}
              storeId={currentStore}
              startDate={profitAndLostData?.start_date || startDate}
              endDate={profitAndLostData?.end_date || endDate}
            />
          </secondaryComponents.Modal>
        </motion.div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};
export default ProfitAndLost;
